/**
 * Collection API hooks - Global sync via Jotai + TanStack Query mutations
 *
 * Architecture:
 * - useCollectionSync() subscribes to Firestore and populates collectionStateAtom
 * - Returns isLoading=true until first snapshot, used by app initialization
 * - enrichedCollectionAtom is a derived atom that combines collection + cache + folders
 * - useCardCachePopulator() fills the cache for cards not yet fetched
 * - All consumers read from the shared Jotai atoms via useCollectionQuery()
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import type { DocumentData } from 'firebase/firestore';
import { collection, orderBy, query } from 'firebase/firestore';
import { useAtom, useAtomValue } from 'jotai';

import { collectionStateAtom, enrichedCollectionAtom } from '@/features/collection/state/collectionAtom';
import { allDeckCardsAtom } from '@/features/decks/state/allDeckCards';
import { listCardsStateAtom } from '@/features/lists/state/listsAtom';
import { starredStateAtom } from '@/features/starred/state/starredAtom';
import { useFirestoreCollectionSync } from '@/hooks/useFirestoreSync';
import { getCardsByIds } from '@/services/cards';
import {
  addCollectionCard,
  addCollectionCards,
  batchDeleteCollectionCards,
  batchUpdateCollectionCards,
  deleteCollectionCard,
  moveCardsToFolder,
  updateCardQuantity,
  updateCollectionCard,
} from '@/services/collection';
import { auth, firestore } from '@/services/firebase';
import { addToFullCardCache, getFullCardCache, isIndexedDBSupported } from '@/services/indexedDbCache';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { CollectionCard } from '@/types/card';
import type { CreateCollectionCard } from '@/types/schemas';

// Re-export state atoms for consumers who need direct access
export { collectionStateAtom, enrichedCollectionAtom } from '@/features/collection/state/collectionAtom';

// ============================================================================
// Sync Hook
// ============================================================================

/**
 * Syncs collection from Firestore to the collectionStateAtom.
 * Returns true while loading (until first snapshot arrives).
 *
 * @param enabled - Whether sync is enabled (typically: isAuthenticated)
 *
 * @example
 * ```tsx
 * const isLoadingCollection = useCollectionSync(isAuthenticated);
 * ```
 */
export function useCollectionSync(enabled: boolean): boolean {
  const transform = useCallback((docs: DocumentData[]): CollectionCard[] => {
    return docs as CollectionCard[];
  }, []);

  const queryFactory = useCallback(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return null;
    return query(collection(firestore, 'users', userId, 'collection'), orderBy('created_at', 'desc'));
  }, []);

  return useFirestoreCollectionSync({
    atom: collectionStateAtom,
    transform,
    queryFactory,
    enabled,
  });
}

// ============================================================================
// Card Cache Populator Hook
// ============================================================================

/**
 * Hook that populates the card cache with Scryfall data for collection AND deck cards.
 * Call this ONCE from a component high in the tree (e.g., useAppInitialization).
 * Automatically fetches missing cards when collection or deck cards change.
 *
 * On startup:
 * 1. Loads cached cards from IndexedDB (if available)
 * 2. Waits for both collection AND deck cards to be synced
 * 3. Merges scryfall IDs from both sources (deduplicated)
 * 4. Fetches missing cards from Firestore
 * 5. Persists newly fetched cards to IndexedDB
 *
 * Returns isLoading=true during the initial fetch (for app loader).
 * After initial load completes, subsequent fetches don't affect isLoading.
 *
 * The enrichedCollectionAtom is a pure derived atom that reads from the cache.
 * This hook is the only place that writes to the cache.
 *
 * @param enabled - Whether to start populating (typically: collection AND deck cards sync is done)
 */
export function useCardCachePopulator(enabled: boolean): boolean {
  const collectionState = useAtomValue(collectionStateAtom);
  const allDeckCardsState = useAtomValue(allDeckCardsAtom);
  const listCardsState = useAtomValue(listCardsStateAtom);
  const starredState = useAtomValue(starredStateAtom);
  const [cardCache, setCardCache] = useAtom(fullCardCacheAtom);

  // Track which IDs we're currently fetching to avoid duplicate requests
  const fetchingRef = useRef<Set<string>>(new Set());

  // Track initial load state - once complete, never goes back to loading
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const hasInitiallyLoadedRef = useRef(false);

  // Track if we've loaded from IndexedDB
  const hasLoadedFromIndexedDBRef = useRef(false);

  // Ref to access current cache in effect without it being a dependency.
  // Having cardCache as a dep caused a cancellation race: effect re-runs
  // while a fetch is in flight → cancelled flag discards valid results →
  // cards never enter cache → isLoading stuck forever.
  const cardCacheRef = useRef(cardCache);
  cardCacheRef.current = cardCache;

  // Load cached cards from IndexedDB on first enable
  useEffect(() => {
    if (!enabled || hasLoadedFromIndexedDBRef.current) return;
    hasLoadedFromIndexedDBRef.current = true;

    if (!isIndexedDBSupported()) return;

    async function loadFromIndexedDB() {
      try {
        const cachedCards = await getFullCardCache();
        if (cachedCards.size > 0) {
          console.log(`[CardCache] Loaded ${cachedCards.size} cards from IndexedDB`);
          setCardCache(cachedCards);
        }
      } catch (error) {
        console.warn('[CardCache] Failed to load from IndexedDB:', error);
      }
    }

    void loadFromIndexedDB();
  }, [enabled, setCardCache]);

  useEffect(() => {
    if (!enabled) return;

    const collectionCards = collectionState.data;
    const deckCards = allDeckCardsState.data;
    const listCards = listCardsState.data;
    const starredCards = starredState.data;

    // Wait for all data sources to be loaded
    if (collectionCards === undefined || deckCards === undefined || listCardsState.loading || starredState.loading) {
      return;
    }

    // If all are empty, initial load is complete
    if (collectionCards.length === 0 && deckCards.length === 0 && listCards.length === 0 && starredCards.size === 0) {
      if (!hasInitiallyLoadedRef.current) {
        hasInitiallyLoadedRef.current = true;
        setIsInitialLoading(false);
      }
      return;
    }

    // Merge unique Scryfall IDs from ALL sources (automatically deduplicates)
    const collectionIds = collectionCards.map((c) => c.scryfall_id);
    const deckCardIds = deckCards.map((c) => c.scryfall_id);
    // List cards: scryfall_id is always present (required field)
    const listCardIds = listCards.map((c) => c.scryfall_id);
    // Starred cards: use scryfall_id when set
    const starredCardIds = Array.from(starredCards.values())
      .filter((c) => c.scryfall_id)
      .map((c) => c.scryfall_id!);
    const scryfallIds = [...new Set([...collectionIds, ...deckCardIds, ...listCardIds, ...starredCardIds])];

    // Find IDs missing from cache AND not currently being fetched
    // Use ref to read current cache without making it a dependency
    const currentCache = cardCacheRef.current;
    const missingIds = scryfallIds.filter((id) => !currentCache.has(id) && !fetchingRef.current.has(id));

    // If no missing IDs and we haven't marked initial load done yet, do it now
    if (missingIds.length === 0) {
      if (!hasInitiallyLoadedRef.current) {
        hasInitiallyLoadedRef.current = true;
        setIsInitialLoading(false);
      }
      return;
    }

    // Mark as fetching
    missingIds.forEach((id) => fetchingRef.current.add(id));

    let cancelled = false;

    async function fetchMissingCards() {
      try {
        const fetchedCards = await getCardsByIds(missingIds);

        // Always update cache with fetched data, even if the effect re-ran.
        // The fetched data is valid regardless — discarding it causes cards
        // to never enter the cache, leaving isLoading stuck forever.
        if (fetchedCards.size > 0) {
          setCardCache((prevCache) => {
            const newCache = new Map(prevCache);
            fetchedCards.forEach((card, id) => {
              newCache.set(id, card);
            });
            return newCache;
          });

          // Persist to IndexedDB
          if (isIndexedDBSupported()) {
            const cardsArray = Array.from(fetchedCards.values());
            await addToFullCardCache(cardsArray);
            console.log(`[CardCache] Persisted ${cardsArray.length} cards to IndexedDB`);
          }
        }

        // Mark initial load complete (only once, and only if this run is
        // still current — a newer run may have additional IDs to fetch)
        if (!cancelled && !hasInitiallyLoadedRef.current) {
          hasInitiallyLoadedRef.current = true;
          setIsInitialLoading(false);
        }
      } catch (err) {
        console.error('Error fetching cards for cache:', err);
        // Still mark initial load as complete on error to not block the app
        if (!hasInitiallyLoadedRef.current) {
          hasInitiallyLoadedRef.current = true;
          setIsInitialLoading(false);
        }
      } finally {
        // Remove from fetching set
        missingIds.forEach((id) => fetchingRef.current.delete(id));
      }
    }

    void fetchMissingCards();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    collectionState.data,
    allDeckCardsState.data,
    listCardsState.data,
    listCardsState.loading,
    starredState.data,
    starredState.loading,
    setCardCache,
  ]);

  // If not enabled or data not loaded yet, we're still in initial loading
  if (!enabled || collectionState.isLoading || allDeckCardsState.isLoading || listCardsState.loading || starredState.loading) {
    return true;
  }

  return isInitialLoading;
}

// ============================================================================
// Public Hooks
// ============================================================================

/**
 * Read-only hook for user's enriched collection.
 * Returns enriched cards with Scryfall data and folder paths.
 * Data comes from the derived enrichedCollectionAtom.
 */
export function useCollectionQuery() {
  const enrichedState = useAtomValue(enrichedCollectionAtom);

  return {
    data: enrichedState.data,
    isLoading: enrichedState.isLoading,
    error: enrichedState.error,
  };
}

/**
 * Mutation hook for adding a single card
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useAddCardMutation() {
  return useMutation({
    mutationFn: (card: CreateCollectionCard) => addCollectionCard(card),
  });
}

/**
 * Mutation hook for adding multiple cards (batch import)
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useAddCardsMutation() {
  return useMutation({
    mutationFn: ({ cards, onBatchCommitted }: { cards: CreateCollectionCard[]; onBatchCommitted?: (n: number) => void }) =>
      addCollectionCards(cards, onBatchCommitted),
  });
}

/**
 * Mutation hook for updating card quantity
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useUpdateQuantityMutation() {
  return useMutation({
    mutationFn: ({ cardId, quantity }: { cardId: string; quantity: number }) => updateCardQuantity(cardId, quantity),
  });
}

/**
 * Mutation hook for deleting a card
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useDeleteCardMutation() {
  return useMutation({
    mutationFn: (cardId: string) => deleteCollectionCard(cardId),
  });
}

/**
 * Mutation hook for moving cards to a folder
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useMoveToFolderMutation() {
  return useMutation({
    mutationFn: ({ cardIds, folderId }: { cardIds: string[]; folderId: string | null }) => moveCardsToFolder(cardIds, folderId),
  });
}

/**
 * Mutation hook for updating a card's fields
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useUpdateCardMutation() {
  return useMutation({
    mutationFn: ({ cardId, data }: { cardId: string; data: Parameters<typeof updateCollectionCard>[1] }) =>
      updateCollectionCard(cardId, data),
  });
}

/**
 * Mutation hook for batch updating multiple cards
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useBatchUpdateCardsMutation() {
  return useMutation({
    mutationFn: ({ cardIds, patch, onProgress }: { cardIds: string[]; patch: Record<string, unknown>; onProgress?: (n: number) => void }) =>
      batchUpdateCollectionCards(cardIds, patch, onProgress),
  });
}

/**
 * Mutation hook for batch deleting multiple cards
 * Note: No cache invalidation needed - real-time subscription auto-updates
 */
export function useBatchDeleteCardsMutation() {
  return useMutation({
    mutationFn: ({ cardIds, onProgress }: { cardIds: string[]; onProgress?: (n: number) => void }) =>
      batchDeleteCollectionCards(cardIds, onProgress),
  });
}
